/* eslint-disable @typescript-eslint/no-explicit-any */

export type ObjectConstructor<T> = new (...args: any[]) => T;

/**
 * Class for pooling Objects of any type
 */
export class GenericObjectPool {
  private static readonly instances = new Map<any, GenericObjectPool>();

  private readonly objConstructorMap: Map<any, () => any>;

  private readonly poolMap: Map<any, Set<any>>;

  constructor() {
    this.objConstructorMap = new Map();
    this.poolMap = new Map();
  }

  /**
   * Set Object construction function for a specific key.
   */
  public setObjConstructFunc<T>(poolKey: ObjectConstructor<T>, func: () => T): void {
    this.objConstructorMap.set(poolKey, func);
  }

  /**
   * Adds an Object to the pool.
   * @param poolKey key used to track object type
   * @param object
   */
  public addObject<T>(poolKey: ObjectConstructor<T>, ...object: T[]): void {
    const pool = this.getPool(poolKey);
    object.forEach((o) => pool.add(o));
  }

  /**
   * Check if an object is in a pool. Mostly for debugging.
   * @param poolKey key used to track object type
   * @param object
   */
  public hasObject<T>(poolKey: ObjectConstructor<T>, ...object: T[]): boolean {
    const pool = this.getPool(poolKey);
    return object.some((obj) => pool.has(obj));
  }

  /**
   * Gets a free Object if any exist.
   * @param poolKey key used to track object type
   */
  public getObject<T>(poolKey: ObjectConstructor<T>): T | undefined {
    const pool = this.getPool(poolKey);
    if (pool.size === 0) {
      const constructFunc = this.objConstructorMap.get(poolKey);
      if (constructFunc) return constructFunc() as T;
      return undefined;
    }

    const value = pool.values().next().value as T;
    pool.delete(value);
    return value;
  }

  /**
   * Gets / Creates pool for key.
   */
  protected getPool<T>(poolKey: ObjectConstructor<T>): Set<T> {
    if (!this.poolMap.has(poolKey)) this.poolMap.set(poolKey, new Set());
    return this.poolMap.get(poolKey) as Set<T>;
  }

  /**
   * Gets count of pooled objects of the given type.
   * @param poolKey
   */
  public getObjectCount<T>(poolKey: ObjectConstructor<T>): number {
    return this.getPool(poolKey).size;
  }

  /**
   * Gets a GenericObjectPool instance by its instanceKey.
   */
  public static getInstanceByKey(instanceKey: any): GenericObjectPool {
    if (!this.instances.has(instanceKey)) {
      this.instances.set(instanceKey, new GenericObjectPool());
    }
    return this.instances.get(instanceKey) as unknown as GenericObjectPool;
  }

  /**
   * Purge tracked GenericObjectPools.
   * @param instanceKeys optional list of specific keys to clear.
   */
  public static purgeInstances(instanceKeys?: any[]): void {
    const keysToPurge = instanceKeys ?? Object.keys(this.instances);
    keysToPurge.forEach((instanceKey) => this.instances.delete(instanceKey));
  }
}
